// Singleton wrapper for the OpenCV coverage check worker.
// Uses MessageChannel for communication because OpenCV.js WASM runtime
// hijacks the worker's main postMessage/onmessage events.

import { CV_COVERAGE_WORKER_SCRIPT_URL } from '../constants/cv-worker'

let _worker = null
let _channel = null
let _requestId = 0
const _pendingRequests = new Map()

// Generation counter — incremented on every new checkCoverageWithWorker call.
// Any in-flight async work (bitmap loading, worker processing) belonging to an older
// generation is silently discarded, so only the LATEST call ever resolves its caller.
let _generation = 0

function _getWorker() {
    if (!_worker) {
        _worker = new Worker(CV_COVERAGE_WORKER_SCRIPT_URL)
        _channel = new MessageChannel()

        // Send dedicated port to worker (before OpenCV hijacks messages)
        _worker.postMessage({ type: 'init-port', port: _channel.port2 }, [_channel.port2])

        // Listen for results on our dedicated port
        _channel.port1.onmessage = (e) => {
            const tReceived = performance.now()
            const tReceivedWall = Date.now()
            const { id, result, tWorkerDoneWall } = e.data
            const pending = _pendingRequests.get(id)
            if (pending) {
                _pendingRequests.delete(id)
                // Discard stale results — a newer request has already superseded this one
                if (pending.generation < _generation) {
                    console.log(`[coverage-checker][main] ⚡ discard stale result id=${id} (gen ${pending.generation} < current ${_generation})`)
                    return
                }
                if (pending.t0) {
                    const total = (tReceived - pending.t0).toFixed(1)
                    const waitWorker = (tReceived - pending.tPostMessage).toFixed(1)
                    // IPC gap = wall-clock time from worker postMessage to main onmessage fire
                    // This is the pure "message in queue but main thread busy" time
                    const ipcGap = tWorkerDoneWall != null
                        ? (tReceivedWall - tWorkerDoneWall) + 'ms'
                        : '?'
                    console.log(`[coverage-checker][main] worker replied @ ${_ts()} → wait-for-worker: ${waitWorker}ms | IPC gap (main busy): ${ipcGap} | TOTAL: ${total}ms`)
                }
                pending.resolve(result)
            }
        }

        _worker.onerror = (err) => {
            console.error('[coverage-checker] worker error:', err)
            _pendingRequests.forEach((p) => p.resolve({ ok: true, coveragePercent: 100, uncoveredEdges: [], uncoveredCorners: [] }))
            _pendingRequests.clear()
        }
    }
    return _worker
}

// Load image URL as ImageBitmap on main thread (has CORS access)
// _loadBitmap, _tplImageCache, _computeScale removed —
// all image fetching, decoding, and resizing now runs inside the worker (off main thread).

function _mark(label, t0) {
    const ms = (performance.now() - t0).toFixed(1)
    console.log(`[coverage-checker][main] ${label}: ${ms}ms`)
    return performance.now()
}

// Wall-clock timestamp for correlating main-thread vs worker logs
function _ts() {
    const d = new Date()
    return `${d.getHours().toString().padStart(2,'0')}:${d.getMinutes().toString().padStart(2,'0')}:${d.getSeconds().toString().padStart(2,'0')}.${d.getMilliseconds().toString().padStart(3,'0')}`
}

// Compute uniform scale so max(W,H) ≤ PROCESS_SIZE.
const PROCESS_SIZE = 600
function _computeScale(W, H) {
    const scale = Math.min(1, PROCESS_SIZE / Math.max(W, H))
    return { scale, pw: Math.round(W * scale), ph: Math.round(H * scale) }
}

/**
 * Run pixel-perfect coverage check via OpenCV worker.
 *
 * Strategy (Option E — transfer pre-decoded ImageBitmap):
 *   - Caller creates ImageBitmap from the already-loaded HTMLImageElement (no re-fetch, ~2-5ms).
 *   - Main thread transfers bitmap zero-copy into worker via postMessage transferables.
 *   - Worker receives pixels directly — no fetch, no decode, no network.
 *   - Template is still fetched+cached by URL in worker (_tplFetchCache).
 *
 * @param {ImageBitmap} artBitmap - pre-decoded artwork bitmap (transferable, will be neutered after send)
 */
export function checkCoverageWithWorker({ artBitmap, templateSrc, placement, templateSize, safezoneRect = null }) {
    _getWorker()

    // Bump generation — stale replies are discarded in onmessage handler.
    const myGen = ++_generation

    const t0 = performance.now()
    console.log(`[coverage-checker][main] START gen=${myGen} @ ${_ts()} | templateSize: ${templateSize.width}x${templateSize.height}`)

    const id = ++_requestId
    return new Promise((resolve) => {
        const tPostMessage = performance.now()
        _pendingRequests.set(id, { resolve, t0, tPostMessage, generation: myGen })
        // Transfer artBitmap zero-copy — after this, artBitmap is neutered on main thread
        _channel.port1.postMessage(
            { id, artBitmap, templateSrc, placement, templateSize, safezoneRect },
            [artBitmap],
        )
        _mark(`postMessage to worker @ ${_ts()} (id=${id} gen=${myGen})`, t0)
    })
}

/**
 * Terminate the coverage worker and release resources.
 */
export function destroyCoverageWorker() {
    if (_worker) {
        _worker.terminate()
        _worker = null
        _channel = null
        _pendingRequests.clear()
    }
}
